import superjson from 'superjson';

export class LocalJSONStorage<T> {
    $inferType!: T;

    constructor(
        public readonly key: string,
        public readonly initialValue: T,
        public readonly storageType: LocalJSONStorage.StorageType = 'sessionStorage',
    ) {}

    getStore(): Storage {
        return window[this.storageType];
    }

    getValue() {
        if (typeof window === 'undefined') return this.initialValue;

        const value = this.getStore().getItem(this.key);
        if (!value) return this.initialValue;

        try {
            return superjson.parse(value) as T;
        } catch (e) {
            console.error(`Failed to parse window.${this.storageType}["${this.key}"]`, e);
            window[this.storageType].removeItem(this.key);

            return this.initialValue;
        }
    }

    setValue(value: T | ((current: T) => T)) {
        if (typeof window === 'undefined') {
            throw new Error('Cannot set localstorage value on server');
        }

        this.getStore().setItem(
            this.key,
            superjson.stringify(
                typeof value === 'function' ? (value as (current: T) => T)(this.getValue()) : value,
            ),
        );
        this.ChangeEvent_dispatch();

        return this;
    }

    clear() {
        if (typeof window === 'undefined')
            throw new Error('Cannot set localstorage value on server');

        window[this.storageType].removeItem(this.key);
        this.ChangeEvent_dispatch();
    }

    private ChangeEvent_dispatch() {
        window.dispatchEvent(
            new LocalJSONStorage_ChangeEvent({
                key: this.key,
                storageType: this.storageType,
            }),
        );
    }

    /** Listens to both current and cross-tab changes and fires the callback
     * @returns A callback that unsubscribes from the events */
    public ChangeEvent_listen(callback: (event: LocalJSONStorage_ChangeEvent) => void): () => void {
        const abort = new AbortController();

        // Cross-tab sync
        window.addEventListener(
            'storage',
            (event: StorageEvent) => {
                if (event.storageArea !== window[this.storageType]) return;
                if (event.key !== this.key) return;

                callback(
                    new LocalJSONStorage_ChangeEvent({
                        key: this.key,
                        storageType: this.storageType,
                    }),
                );
            },
            { signal: abort.signal },
        );

        // Current-tab sync
        window.addEventListener(
            LocalJSONStorage_ChangeEvent.KEY,
            (e: Event) => {
                if (e instanceof LocalJSONStorage_ChangeEvent === false) return;
                if (e.detail.storageType !== this.storageType) return;
                if (e.detail.key !== this.key) return;

                callback(e);
            },
            { signal: abort.signal },
        );
        return () => abort.abort();
    }
}
export declare namespace LocalJSONStorage {
    export type StorageType = 'sessionStorage' | 'localStorage';
}

export function safeFn<T extends (...args: any[]) => any>(fn: T) {
    return (
        ...args: Parameters<T>
    ): Readonly<
        | {
              ok: true;
              data: ReturnType<T>;
          }
        | {
              ok: false;
              error: unknown;
          }
    > => {
        try {
            return { ok: true, data: fn(...args) };
        } catch (error) {
            return { ok: false, error };
        }
    };
}

export declare namespace LocalJSONStorage_ChangeEvent {
    export type Data = {
        key: string;
        storageType: LocalJSONStorage.StorageType;
    };
}
export class LocalJSONStorage_ChangeEvent extends CustomEvent<LocalJSONStorage_ChangeEvent.Data> {
    static KEY = `Funkey:LocalJSONStorage_ChangeEvent`;

    constructor(data: LocalJSONStorage_ChangeEvent.Data) {
        super(LocalJSONStorage_ChangeEvent.KEY, {
            detail: data,
            cancelable: false,
        });
    }
}
